import React, { useEffect, useState } from "react";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Grid,
	Switch,
	TextField,
	Typography
} from "@mui/material";
import { Email } from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../../services/api";
import QueueSelect from "../QueueSelect";

// Conexão do canal de E-MAIL (26/10/2026): caixa IMAP (recebe) + SMTP (envia).
// A senha vai para o backend e fica cifrada; nunca volta para o navegador
// (ao editar, campo em branco = mantém a senha gravada).

const VAZIO = {
	name: "",
	emailFromAddress: "",
	emailFromName: "",
	emailUser: "",
	emailPassword: "",
	emailImapHost: "",
	emailImapPort: 993,
	emailImapSecure: true,
	emailSmtpHost: "",
	emailSmtpPort: 587,
	emailSmtpSecure: false,
	emailSignature: "",
	color: "#004f9f"
};

// Preenche servidores conhecidos a partir do domínio do e-mail.
const PROVEDORES = {
	"gmail.com": ["imap.gmail.com", "smtp.gmail.com"],
	"outlook.com": ["outlook.office365.com", "smtp.office365.com"],
	"hotmail.com": ["outlook.office365.com", "smtp.office365.com"],
	"yahoo.com": ["imap.mail.yahoo.com", "smtp.mail.yahoo.com"],
	"yahoo.com.br": ["imap.mail.yahoo.com", "smtp.mail.yahoo.com"]
};

const EmailChannelModal = ({ open, onClose, whatsAppId }) => {
	const [form, setForm] = useState(VAZIO);
	const [queueIds, setQueueIds] = useState([]);
	const [hasPassword, setHasPassword] = useState(false);
	const [lastError, setLastError] = useState("");
	const [salvando, setSalvando] = useState(false);
	const [testando, setTestando] = useState(false);
	const [teste, setTeste] = useState(null);

	useEffect(() => {
		if (!open) return;
		setTeste(null);
		if (!whatsAppId) {
			setForm(VAZIO);
			setQueueIds([]);
			setHasPassword(false);
			setLastError("");
			return;
		}
		(async () => {
			try {
				const { data } = await api.get(`/email-channels/${whatsAppId}`);
				const f = { ...VAZIO };
				Object.keys(VAZIO).forEach(k => {
					if (data[k] !== null && data[k] !== undefined) f[k] = data[k];
				});
				f.emailPassword = "";
				setForm(f);
				setQueueIds((data.queues || []).map(q => q.id));
				setHasPassword(!!data.hasPassword);
				setLastError(data.emailLastError || "");
			} catch (err) {
				toast.error("Não foi possível carregar a conexão de e-mail");
			}
		})();
	}, [open, whatsAppId]);

	const muda = campo => e => {
		const valor = e.target.type === "checkbox" ? e.target.checked : e.target.value;
		setForm(prev => {
			const novo = { ...prev, [campo]: valor };
			if (campo === "emailFromAddress") {
				const dominio = String(valor).split("@")[1]?.toLowerCase();
				if (!prev.emailUser || prev.emailUser === prev.emailFromAddress) novo.emailUser = valor;
				if (dominio && PROVEDORES[dominio] && !prev.emailImapHost && !prev.emailSmtpHost) {
					[novo.emailImapHost, novo.emailSmtpHost] = PROVEDORES[dominio];
				}
			}
			if (campo === "emailImapSecure") novo.emailImapPort = valor ? 993 : 143;
			if (campo === "emailSmtpSecure") novo.emailSmtpPort = valor ? 465 : 587;
			return novo;
		});
	};

	const testar = async () => {
		setTestando(true);
		setTeste(null);
		try {
			const { data } = await api.post("/email-channels/test", { ...form, id: whatsAppId || undefined });
			setTeste(data);
		} catch (err) {
			toast.error(err?.response?.data?.error || "Falha ao testar");
		} finally {
			setTestando(false);
		}
	};

	const salvar = async () => {
		setSalvando(true);
		try {
			const corpo = { ...form, queueIds };
			if (!corpo.emailPassword) delete corpo.emailPassword;
			if (whatsAppId) await api.put(`/email-channels/${whatsAppId}`, corpo);
			else await api.post("/email-channels", corpo);
			toast.success("Conexão de e-mail salva — conectando à caixa…");
			onClose(true);
		} catch (err) {
			toast.error(err?.response?.data?.error || "Não foi possível salvar");
		} finally {
			setSalvando(false);
		}
	};

	const campo = (nome, label, props = {}) => (
		<TextField
			label={label}
			value={form[nome] ?? ""}
			onChange={muda(nome)}
			fullWidth
			size="small"
			margin="dense"
			inputProps={{ "data-campo": nome }}
			{...props}
		/>
	);

	const linhaTeste = (rotulo, r) =>
		r && (
			<Alert severity={r.ok ? "success" : "error"} sx={{ mb: 1 }}>
				<b>{rotulo}:</b>{" "}
				{r.ok
					? rotulo === "Entrada (IMAP)"
						? `conectou — ${r.mensagens ?? 0} mensagem(ns) na caixa${r.idle ? ", aviso imediato (IDLE)" : ", sem IDLE (verifica a cada minuto)"}`
						: "conectou e autenticou"
					: r.erro || "falhou"}
			</Alert>
		);

	return (
		<Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
			<DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
				<Email sx={{ color: "#004f9f" }} />
				{whatsAppId ? "Editar conexão de e-mail" : "Nova conexão de e-mail"}
			</DialogTitle>
			<DialogContent dividers>
				{lastError && (
					<Alert severity="warning" sx={{ mb: 2 }}>
						Último erro da caixa: {lastError}
					</Alert>
				)}
				<Grid container spacing={2}>
					<Grid item xs={12} md={6}>
						{campo("name", "Nome da conexão", { required: true })}
						{campo("emailFromAddress", "E-mail da caixa (remetente)", { type: "email", required: true })}
						{campo("emailFromName", "Nome de exibição (ex.: Atendimento Sabe)")}
						{campo("emailUser", "Usuário (login)")}
						{campo("emailPassword", hasPassword ? "Senha (em branco = manter a atual)" : "Senha", {
							type: "password",
							autoComplete: "new-password",
							required: !hasPassword
						})}
						<Typography variant="caption" color="textSecondary">
							Gmail/Outlook com verificação em duas etapas: use uma "senha de app".
						</Typography>
						{campo("emailSignature", "Assinatura (vai no fim de cada resposta)", { multiline: true, minRows: 3 })}
					</Grid>
					<Grid item xs={12} md={6}>
						<Typography variant="subtitle2" sx={{ mt: 1 }}>Entrada (IMAP)</Typography>
						<Box sx={{ display: "flex", gap: 1 }}>
							{campo("emailImapHost", "Servidor IMAP")}
							{campo("emailImapPort", "Porta", { type: "number", sx: { width: 110 } })}
						</Box>
						<FormControlLabel
							control={<Switch checked={!!form.emailImapSecure} onChange={muda("emailImapSecure")} />}
							label="SSL/TLS direto (993)"
						/>
						<Typography variant="subtitle2" sx={{ mt: 1 }}>Saída (SMTP)</Typography>
						<Box sx={{ display: "flex", gap: 1 }}>
							{campo("emailSmtpHost", "Servidor SMTP")}
							{campo("emailSmtpPort", "Porta", { type: "number", sx: { width: 110 } })}
						</Box>
						<FormControlLabel
							control={<Switch checked={!!form.emailSmtpSecure} onChange={muda("emailSmtpSecure")} />}
							label="SSL/TLS direto (465) — desligado = STARTTLS (587)"
						/>
						<Box sx={{ mt: 1 }}>
							<QueueSelect selectedQueueIds={queueIds} onChange={setQueueIds} />
						</Box>
					</Grid>
				</Grid>
				{teste && (
					<Box sx={{ mt: 2 }} data-teste-resultado>
						{linhaTeste("Entrada (IMAP)", teste.imap)}
						{linhaTeste("Saída (SMTP)", teste.smtp)}
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={testar} disabled={testando || salvando} data-acao="testar">
					{testando ? <CircularProgress size={20} /> : "Testar conexão"}
				</Button>
				<Box sx={{ flex: 1 }} />
				<Button onClick={() => onClose(false)} disabled={salvando}>Cancelar</Button>
				<Button variant="contained" onClick={salvar} disabled={salvando} data-acao="salvar">
					{salvando ? <CircularProgress size={20} /> : "Salvar"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default EmailChannelModal;
